import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import { Box, Chip, TextField, FormControl } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { debounce } from "lodash";

const useStyles = makeStyles((theme) => ({
  formControl: {
    width: "100%",
  },
  inputRoot: {
    '&.MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: theme.palette.background.paper,
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.divider,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.divider,
      },
      '& input': {
        padding: '10.5px 14px',
        '&::placeholder': {
          color: theme.palette.text.secondary,
          opacity: 1,
        },
      },
    },
  },
  chip: {
    margin: theme.spacing(0.5),
    borderRadius: 4,
    fontWeight: 500,
    fontSize: '0.875rem',
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.text.primary,
  },
  autoComplete: {
    '& .MuiAutocomplete-inputRoot': {
      padding: '2px 8px',
    },
    '& .MuiAutocomplete-endAdornment': {
      right: 8,
    },
  },
}));

export function UsersFilter({ onFiltered, initialUsers }) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [selecteds, setSelecteds] = useState([]);
  const isFirstRender = useRef(true);
  
  useEffect(() => {
    async function fetchData() {
      await loadUsers();
    }
    fetchData();
  }, []);

  useEffect(() => {
    // Solo aplicamos initialUsers si tenemos users cargados
    if (!isFirstRender.current && 
        Array.isArray(initialUsers) && 
        Array.isArray(users) && 
        users.length > 0) {
      
      const validInitialUsers = initialUsers.filter(
        u => typeof u === 'object' && u !== null && u.id
      );
      
      if (validInitialUsers.length > 0) {
        console.log("Aplicando initialUsers:", validInitialUsers);
        setSelecteds(validInitialUsers);
      }
    }
    
    isFirstRender.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUsers, users]);

  const loadUsers = async () => {
    try {
      const { data } = await api.get(`/users/list`);
      if (Array.isArray(data)) {
        const userList = data
          .filter(u => u && u.id !== user.id)
          .map((u) => ({ id: u.id, name: u.name }));
        
        console.log("Usuarios cargados:", userList.length);
        setUsers(userList);
      } else {
        console.warn("La API no devolvió un array para usuarios");
        setUsers([]);
      }
    } catch (err) {
      console.error("Error al cargar usuarios:", err);
      toastError(err);
      setUsers([]);
    }
  };

  const onChange = async (value) => {
    // Nos aseguramos de que value sea un array
    const safeValue = Array.isArray(value) ? value : [];
    
    console.log("UsersFilter - onChange:", {
      valueLength: safeValue.length,
      valuesWithIds: safeValue.map(v => v?.id || 'no id')
    });
    
    setSelecteds(safeValue);
    // No llamamos a onFiltered aquí, lo manejamos externamente
  };

  // Utilizamos el debounce para evitar múltiples llamadas
  const debouncedOnChange = useCallback(
    debounce((value) => {
      // Nos aseguramos de que value sea un array
      const safeValue = Array.isArray(value) ? value : [];
      
      console.log("UsersFilter - debouncedOnChange:", {
        valueLength: safeValue.length,
        ids: safeValue.map(v => v?.id || 'no id')
      });
      
      if (onFiltered) {
        onFiltered(safeValue);
      }
    }, 500), // 500ms delay
    [onFiltered]
  );

  useEffect(() => {
    // Cuando cambian los seleccionados, llamamos a la función debounced
    // pero nos aseguramos de que selecteds sea un array
    const safeSelecteds = Array.isArray(selecteds) ? selecteds : [];
    debouncedOnChange(safeSelecteds);
    
    // Cleanup function
    return () => debouncedOnChange.cancel();
  }, [selecteds, debouncedOnChange]);

  return (
    <Box style={{ padding: '0px 0px 16px' }}>
      <FormControl className={classes.formControl}>
        <Autocomplete
          multiple
          size="small"
          options={users || []}
          value={selecteds || []}
          onChange={(e, v) => onChange(v || [])}
          getOptionLabel={(option) => option?.name || ''}
          className={classes.autoComplete}
          getOptionSelected={(option, value) => {
            return (
              option?.id === value?.id ||
              option?.name?.toLowerCase() === value?.name?.toLowerCase()
            );
          }}
          renderTags={(value, getUserProps) =>
            Array.isArray(value) && value.map((option, index) => (
              <Chip
                key={option?.id || index}
                variant="outlined"
                label={option?.name || ''}
                {...getUserProps({ index })}
                size="small"
                className={classes.chip}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Filtro por usuarios"
              InputProps={{
                ...params.InputProps,
                classes: { root: classes.inputRoot },
              }}
            />
          )}
        />
      </FormControl>
    </Box>
  );
}